import React, { useEffect, useState } from 'react';
import { Zap, TrendingUp, Target, Sparkles } from 'lucide-react';
interface Feature {
  id: number;
  icon: React.ReactNode;
  title: string;
  tagline: string;
  description: string;
  stat: string;
  statLabel: string;
}
export function FeatureSectionBold() {
  const [activeFeature, setActiveFeature] = useState(0);
  const [displayStat, setDisplayStat] = useState('1,247');
  const features: Feature[] = [{
    id: 0,
    icon: <Zap className="w-8 h-8" strokeWidth={2.5} data-id="element-298" />,
    title: 'Automated Offers',
    tagline: 'Send 1,000 offers while you sleep',
    description: 'Our AI analyzes every MLS listing, generates personalized offers, and sends them automatically. No manual work, no missed opportunities.',
    stat: '1,247',
    statLabel: 'Offers sent this week'
  }, {
    id: 1,
    icon: <Target className="w-8 h-8" strokeWidth={2.5} data-id="element-299" />,
    title: 'Smart Targeting',
    tagline: 'Only reach motivated sellers',
    description: 'Machine learning identifies properties with the highest probability of acceptance based on days on market, price drops, and seller behavior.',
    stat: '7.2%',
    statLabel: 'Average response rate'
  }, {
    id: 2,
    icon: <TrendingUp className="w-8 h-8" strokeWidth={2.5} data-id="element-300" />,
    title: 'Deal Pipeline',
    tagline: 'Track every conversation',
    description: 'See every offer, response, and follow-up in one place. Know exactly where each deal stands and what action to take next.',
    stat: '89',
    statLabel: 'Active conversations'
  }, {
    id: 3,
    icon: <Sparkles className="w-8 h-8" strokeWidth={2.5} data-id="element-301" />,
    title: 'AI Follow-Ups',
    tagline: 'Never let a deal go cold',
    description: 'Automated follow-up sequences keep you top-of-mind with agents. Personalized messages sent at optimal times for maximum engagement.',
    stat: '3.2x',
    statLabel: 'More responses vs manual'
  }];
  const activeFeatureData = features[activeFeature];
  // Animate stat change
  useEffect(() => {
    setDisplayStat(activeFeatureData.stat);
  }, [activeFeature, activeFeatureData.stat]);
  return <section className="relative w-full bg-[#070707] overflow-hidden" data-id="element-302">
      {/* Enhanced layered background */}
      <div className="absolute inset-0" data-id="element-303">
        {/* Radial glow - top left */}
        <div className="absolute -top-40 -left-40 w-[600px] h-[600px] bg-[#3ab795] rounded-full blur-[150px] opacity-10 animate-pulse-slow" data-id="element-304" />

        {/* Radial glow - bottom right */}
        <div className="absolute -bottom-40 -right-40 w-[800px] h-[800px] bg-[#3ab795] rounded-full blur-[200px] opacity-15 animate-pulse-slow" style={{
        animationDelay: '1s'
      }} data-id="element-305" />

        {/* Mesh gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#3ab795]/5 via-transparent to-[#3ab795]/10" data-id="element-306" />

        {/* Subtle noise texture */}
        <div className="absolute inset-0 opacity-[0.015] mix-blend-overlay" style={{
        backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' /%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)' /%3E%3C/svg%3E\")",
        backgroundSize: '200px 200px'
      }} data-id="element-307" />
      </div>

      <div className="relative z-10 max-w-[1600px] mx-auto px-6 sm:px-8 lg:px-12 py-24 lg:py-32" data-id="element-308">
        {/* Header */}
        <div className="max-w-3xl mb-20" data-id="element-309">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#3ab795]/10 border border-[#3ab795]/30 rounded-full mb-6 backdrop-blur-sm" data-id="element-310">
            <div className="w-2 h-2 rounded-full bg-[#3ab795] animate-pulse" data-id="element-311" />
            <span className="text-sm font-bold text-[#3ab795] tracking-wider uppercase" data-id="element-312">
              Platform Features
            </span>
          </div>

          <h2 className="text-5xl sm:text-6xl lg:text-7xl font-bold mb-6 leading-[1.1] tracking-tight" data-id="element-313">
            <span className="block text-white drop-shadow-[0_2px_10px_rgba(255,255,255,0.1)]" data-id="element-314">
              Your unfair
            </span>
            <span className="block text-[#3ab795] drop-shadow-[0_0_40px_rgba(58,183,149,0.6)]" data-id="element-315">
              advantage
            </span>
          </h2>

          <p className="text-xl text-gray-400 leading-relaxed" data-id="element-316">
            While other investors manually send 10 offers a week, you'll send
            1,000. That's not working harder—that's working smarter.
          </p>
        </div>

        {/* Main Content Grid */}
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16" data-id="element-317">
          {/* Left: Feature Navigation */}
          <div className="lg:col-span-5 space-y-3" data-id="element-318">
            {features.map((feature, index) => <button key={feature.id} onClick={() => setActiveFeature(feature.id)} className={`w-full text-left group transition-all duration-500 ${activeFeature === feature.id ? 'scale-100' : 'scale-95 opacity-60 hover:opacity-100 hover:scale-98'}`} style={{
            transitionDelay: `${index * 50}ms`
          }} data-id="element-319">
                <div className="relative" data-id="element-320">
                  {/* Glow effect behind active card */}
                  {activeFeature === feature.id && <div className="absolute -inset-1 bg-gradient-to-r from-[#3ab795] to-[#2d9f7e] rounded-3xl blur-xl opacity-30 animate-pulse-slow" data-id="element-321" />}

                  <div className={`relative p-8 rounded-3xl transition-all duration-500 ${activeFeature === feature.id ? 'bg-gradient-to-br from-[#1a1a1a] to-[#0f0f0f] border-2 border-[#3ab795] shadow-[0_8px_32px_rgba(0,0,0,0.4),inset_0_1px_0_rgba(255,255,255,0.05)]' : 'bg-[#0a0a0a] border-2 border-white/5 hover:border-white/10 hover:bg-[#0d0d0d]'}`} data-id="element-322">
                    {/* Inner glow for active card */}
                    {activeFeature === feature.id && <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-[#3ab795]/10 via-transparent to-transparent pointer-events-none" data-id="element-323" />}

                    {/* Icon */}
                    <div className={`inline-flex items-center justify-center w-16 h-16 rounded-2xl mb-6 transition-all duration-500 ${activeFeature === feature.id ? 'bg-[#3ab795] text-white scale-110 shadow-[0_0_30px_rgba(58,183,149,0.5)]' : 'bg-white/5 text-[#3ab795] group-hover:bg-white/10 group-hover:scale-105'}`} style={{
                  animation: activeFeature === feature.id ? 'bounce-subtle 0.6s ease-out' : 'none'
                }} data-id="element-324">
                      {feature.icon}
                    </div>

                    {/* Content */}
                    <div className="relative space-y-3" data-id="element-325">
                      <h3 className={`text-2xl font-bold transition-colors duration-300 ${activeFeature === feature.id ? 'text-white' : 'text-gray-300 group-hover:text-white'}`} data-id="element-326">
                        {feature.title}
                      </h3>

                      <p className={`text-lg font-medium transition-all duration-500 ${activeFeature === feature.id ? 'text-[#3ab795] opacity-100 max-h-20' : 'text-gray-500 opacity-0 max-h-0 overflow-hidden'}`} data-id="element-327">
                        {feature.tagline}
                      </p>

                      {/* Active indicator */}
                      {activeFeature === feature.id && <div className="flex items-center gap-2 pt-2" data-id="element-328">
                          <div className="h-1 w-12 bg-[#3ab795] rounded-full shadow-[0_0_10px_rgba(58,183,149,0.6)]" data-id="element-329" />
                          <span className="text-xs text-gray-500 uppercase tracking-wider" data-id="element-330">
                            Active
                          </span>
                        </div>}
                    </div>
                  </div>
                </div>
              </button>)}
          </div>

          {/* Right: Feature Details */}
          <div className="lg:col-span-7" data-id="element-331">
            <div className="sticky top-8 space-y-8" data-id="element-332">
              {/* Large Stat Card */}
              <div className="relative p-12 rounded-3xl bg-gradient-to-br from-[#1a1a1a] via-[#0f0f0f] to-[#0a0a0a] border-2 border-[#3ab795]/30 overflow-hidden backdrop-blur-xl shadow-[0_20px_60px_rgba(0,0,0,0.5),inset_0_1px_0_rgba(255,255,255,0.05)]" data-id="element-333">
                {/* Animated background gradient */}
                <div className="absolute inset-0 bg-gradient-to-br from-[#3ab795]/10 via-transparent to-transparent opacity-50" data-id="element-334" />

                {/* Pulsing glow effect */}
                <div className="absolute -top-24 -right-24 w-64 h-64 bg-[#3ab795]/20 rounded-full blur-3xl animate-pulse-slow" data-id="element-335" />

                <div className="relative z-10 space-y-8" data-id="element-336">
                  {/* Stat */}
                  <div data-id="element-337">
                    <div className="text-7xl sm:text-8xl font-bold text-white mb-3 tracking-tight drop-shadow-[0_0_30px_rgba(58,183,149,0.5)] transition-all duration-500" data-id="element-338">
                      {displayStat}
                    </div>
                    <div className="text-xl text-[#3ab795] font-medium" data-id="element-339">
                      {activeFeatureData.statLabel}
                    </div>
                  </div>

                  {/* Description */}
                  <div className="space-y-4 pt-6 border-t border-white/10" data-id="element-340">
                    <h4 className="text-2xl font-bold text-white" data-id="element-341">
                      {activeFeatureData.tagline}
                    </h4>
                    <p className="text-lg text-gray-400 leading-relaxed" data-id="element-342">
                      {activeFeatureData.description}
                    </p>
                  </div>

                  {/* Mini dashboard preview */}
                  <div className="grid grid-cols-3 gap-4 pt-6" data-id="element-343">
                    {[{
                    value: '247',
                    label: 'This Week',
                    color: 'white'
                  }, {
                    value: '18',
                    label: 'Responses',
                    color: 'teal'
                  }, {
                    value: '3',
                    label: 'Closed',
                    color: 'white'
                  }].map((item, idx) => <div key={idx} className="p-4 rounded-xl bg-white/5 border border-white/10 backdrop-blur-sm hover:bg-white/10 hover:border-white/20 transition-all duration-300" style={{
                    transitionDelay: `${idx * 100}ms`
                  }} data-id="element-344">
                        <div className={`text-2xl font-bold mb-1 ${item.color === 'teal' ? 'text-[#3ab795]' : 'text-white'}`} data-id="element-345">
                          {item.value}
                        </div>
                        <div className="text-xs text-gray-500 uppercase tracking-wide" data-id="element-346">
                          {item.label}
                        </div>
                      </div>)}
                  </div>
                </div>
              </div>

              {/* CTA */}
              <div className="flex items-center gap-4" data-id="element-347">
                <a href="/trial" className="flex-1 group relative px-8 py-5 bg-[#3ab795] text-white font-bold text-lg rounded-2xl transition-all duration-300 hover:bg-[#2d9f7e] hover:scale-105 hover:shadow-[0_12px_40px_rgba(58,183,149,0.4)] overflow-hidden" data-id="element-348">
                  <span className="relative z-10 flex items-center justify-center gap-3" data-id="element-349">
                    Start Free Trial
                    <svg className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-350">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M17 8l4 4m0 0l-4 4m4-4H3" data-id="element-351" />
                    </svg>
                  </span>
                  {/* Shine effect */}
                  <div className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-1000 bg-gradient-to-r from-transparent via-white/20 to-transparent" data-id="element-352" />
                </a>

                <a href="#pricing" className="px-8 py-5 bg-white/5 text-white font-bold text-lg rounded-2xl border-2 border-white/10 backdrop-blur-sm transition-all duration-300 hover:bg-white/10 hover:border-white/20 hover:scale-105" data-id="element-353">
                  See Pricing
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}